import { applyClaimAdjustment } from "./claim-adjustment";
import type { PersonInputs } from "./types";
import { resolvePia } from "./pia";

/**
 * Find the age at which the cumulative dollars from the LATER claim strategy
 * overtake the cumulative dollars from the EARLIER claim strategy.
 *
 * Returns null if no crossover occurs by age 100 (defensive cap).
 *
 * This walks one month at a time with COLA applied annually — mirrors the cola module
 * to keep the break-even consistent with the break-even chart axes the UI renders.
 */
export function breakEvenAge(params: {
  person: PersonInputs;
  earlierClaimAge: number;
  laterClaimAge: number;
  colaRate: number;
}): number | null {
  const { person, earlierClaimAge, laterClaimAge, colaRate } = params;
  if (laterClaimAge <= earlierClaimAge) return null;

  const birthYear = new Date(person.dob).getUTCFullYear();
  const pia = resolvePia(person);

  const earlier = applyClaimAdjustment({ pia, birthYear, claimAge: earlierClaimAge });
  const later = applyClaimAdjustment({ pia, birthYear, claimAge: laterClaimAge });

  const earlierStartMonth = Math.round(earlierClaimAge * 12);
  const laterStartMonth = Math.round(laterClaimAge * 12);
  const horizonMonth = 100 * 12;

  let earlierCum = 0;
  let laterCum = 0;
  let earlierMonthly = earlier.monthlyBenefit;
  let laterMonthly = later.monthlyBenefit;

  for (let m = earlierStartMonth; m < horizonMonth; m++) {
    earlierCum += earlierMonthly;
    if (m >= laterStartMonth) laterCum += laterMonthly;

    if (laterCum >= earlierCum && m >= laterStartMonth) {
      return m / 12;
    }

    if ((m - earlierStartMonth + 1) % 12 === 0) earlierMonthly *= 1 + colaRate;
    if (m >= laterStartMonth && (m - laterStartMonth + 1) % 12 === 0) {
      laterMonthly *= 1 + colaRate;
    }
  }
  return null;
}
